import { Link, useParams } from "@tanstack/react-router";
import type { ReactNode } from "react";

import { can } from "@devengo/shared";

import { formatCuit } from "../lib/format";
import { useCompanies } from "../lib/hooks";
import { ROLE_LABELS, useBranding, useCurrentUser, useLogout } from "../lib/session";
import { Button } from "./ui";

const headerLink =
  "rounded-md px-2.5 py-1 text-sm text-white/85 hover:bg-white/10 [&.active]:bg-white/15 [&.active]:font-semibold [&.active]:text-white";

const navLink =
  "rounded-md px-2.5 py-1.5 text-sm text-brand-text hover:bg-brand-tint [&.active]:bg-brand-tint [&.active]:font-semibold [&.active]:text-brand-primary";

export function AppShell({ children }: { children: ReactNode }) {
  const branding = useBranding();
  const user = useCurrentUser();
  const logout = useLogout();
  const { companyId } = useParams({ strict: false });
  const companies = useCompanies();
  const company = companies.data?.find((c) => c.id === companyId);

  return (
    <div className="flex min-h-screen flex-col">
      <header className="bg-brand-primary text-white">
        <div className="mx-auto flex max-w-7xl flex-wrap items-center justify-between gap-3 px-4 py-2.5">
          <Link to="/" className="text-lg font-semibold tracking-tight">
            {branding.displayName}
          </Link>
          {/* Studio-wide sections up here; the row below is only the chosen company's. */}
          <nav
            aria-label="Secciones del estudio"
            className="flex flex-1 flex-wrap items-center gap-1"
          >
            <Link to="/" className={headerLink} activeOptions={{ exact: true }}>
              Empresas
            </Link>
            <Link to="/contrapartes" className={headerLink}>
              Contrapartes
            </Link>
            {can(user.role, "manageReceivables") ? (
              <Link to="/cobranzas" className={headerLink}>
                Cobranzas
              </Link>
            ) : null}
            {can(user.role, "viewAudit") ? (
              <Link to="/auditoria" className={headerLink}>
                Auditoría
              </Link>
            ) : null}
            {can(user.role, "connectAssistant") ? (
              <Link to="/asistentes" className={headerLink}>
                Asistentes
              </Link>
            ) : null}
            {can(user.role, "manageUsers") ? (
              <Link to="/estudio" className={headerLink}>
                Estudio
              </Link>
            ) : null}
          </nav>
          <div className="flex items-center gap-3 text-sm">
            <Link to="/cuenta" className="hover:underline" title="Mi cuenta: clave y sesiones">
              {user.name} <span className="opacity-75">· {ROLE_LABELS[user.role]}</span>
            </Link>
            <button
              type="button"
              className="rounded border border-white/40 px-2 py-0.5 hover:bg-white/10"
              onClick={() =>
                void logout().catch(() =>
                  window.alert(
                    "No pudimos cerrar la sesión. Revisá tu conexión e intentá de nuevo.",
                  ),
                )
              }
            >
              Salir
            </button>
          </div>
        </div>
      </header>
      {/* Only inside a company: its sections. Companies are searched in Empresas (Martín, 2026-10-20). */}
      {companyId ? (
        <nav className="border-b border-brand-line bg-white">
          <div className="mx-auto flex max-w-7xl flex-wrap items-center gap-2 px-4 py-2">
            <Link to="/empresas/$companyId/comprobantes" params={{ companyId }} className={navLink}>
              Comprobantes
            </Link>
            <Link
              to="/empresas/$companyId/registro-compras"
              params={{ companyId }}
              className={navLink}
            >
              Registro de compras
            </Link>
            <Link
              to="/empresas/$companyId/liquidaciones"
              params={{ companyId }}
              className={navLink}
            >
              Liquidaciones
            </Link>
            <Link to="/empresas/$companyId/importar" params={{ companyId }} className={navLink}>
              Importar
            </Link>
            <Link to="/empresas/$companyId/caja" params={{ companyId }} className={navLink}>
              Caja IVA
            </Link>
            <Link to="/empresas/$companyId/libros" params={{ companyId }} className={navLink}>
              Libros IVA
            </Link>
            <Link
              to="/empresas/$companyId/cuentas-tributarias"
              params={{ companyId }}
              className={navLink}
            >
              Cuentas tributarias
            </Link>
            <Link
              to="/empresas/$companyId/planes-de-pago"
              params={{ companyId }}
              className={navLink}
            >
              Planes de pago
            </Link>
            <Link to="/empresas/$companyId/periodos" params={{ companyId }} className={navLink}>
              Períodos
            </Link>
            <Link to="/empresas/$companyId/datos" params={{ companyId }} className={navLink}>
              Contacto
            </Link>
          </div>
        </nav>
      ) : null}
      <main className="mx-auto w-full max-w-7xl flex-1 px-4 py-5">
        {company ? (
          <p className="mb-1 text-xs uppercase tracking-wide text-brand-muted">
            {company.legalName} · CUIT {formatCuit(company.cuit)}
          </p>
        ) : null}
        {children}
      </main>
    </div>
  );
}

export function FullPageMessage({ title, children }: { title: string; children?: ReactNode }) {
  return (
    <div className="flex min-h-screen items-center justify-center p-6">
      <div className="max-w-md text-center">
        <h1 className="text-xl font-semibold">{title}</h1>
        {children ? <div className="mt-2 text-sm text-brand-muted">{children}</div> : null}
        <Button variant="secondary" className="mt-4" onClick={() => window.location.reload()}>
          Reintentar
        </Button>
      </div>
    </div>
  );
}
